// All the logic that has to do with displaying apartment fields.

import { sprintf } from "sprintf-js";

import translate from "../../lib/translate";
import globals from "../../lib/globals";
import {
  formatStringDecimal,
  formatStringArea,
  formatStringInt,
} from "../../lib/format";

export default {
  methods: {
    apartmentDisplayType: function (apartment) {
      return {
        rental: "Vuokra (asuntotyyppi)",
        "right-of-occupancy": "ASO",
        "owner-occupancy": "Omistus",
      }[apartment.type];
    },
    apartmentDisplayPriceTitle: function (apartment) {
      return {
        rental: "Vuokra",
        "right-of-occupancy": "Vastike",
        "owner-occupancy": "Velaton hinta",
      }[apartment.type];
    },
    apartmentDisplayPrice: function (apartment) {
      const priceByType = {
        rental: apartment.dg_vastikealennus ?
          String(Math.round((Number(apartment.mpc_vuokra) - Number(apartment.dg_vastikealennus)) * 100) / 100) :
          apartment.mpc_vuokra,
        "right-of-occupancy": apartment.dg_vastikealennus ?
          String(Math.round((Number(apartment.mpc_vastike) - Number(apartment.dg_vastikealennus)) * 100) / 100) :
          apartment.mpc_vastike,
        "owner-occupancy": apartment.ava_Velatonhinta,
      };

      if (!priceByType[apartment.type]) {
        return "-";
      }

      if (apartment.type === "owner-occupancy") {
        return `${formatStringInt(priceByType[apartment.type])} €`;
      } else {
        return `${formatStringDecimal(
          priceByType[apartment.type]
        )} €/${translate("kk")}`;
      }
    },
    apartmentDisplayColor: function (apartment) {
      return (
        {
          rental: "green",
          "right-of-occupancy": "green",
          "owner-occupancy": "green",
        }[apartment.type] || false
      );
    },
    apartmentDisplayClassBadge: function (apartment, modifiers = []) {
      const color = this.apartmentDisplayColor(apartment);
      return [
        "btn",
        "btn--round",
        color ? `btn--${color}` : null,
        ...modifiers,
      ];
    },
    apartmentDisplayAvailable: function (apartment, fullSentences = false) {
      const strConstructionDate = apartment.housing_locality.mpc_valmistumispaiva;
      const dateConstructionDate = new Date(strConstructionDate);
      if (new Date() <= dateConstructionDate) {
        const strDateConstructionLocalized = dateConstructionDate.toLocaleDateString(globals.language);
        return fullSentences
          ? sprintf(translate("Arvioitu valmistuminen %s"), strDateConstructionLocalized)
          : strDateConstructionLocalized;
      }
      if (!apartment.availableInFuture) {
        return fullSentences ? translate("Heti vapaa") : translate("Heti");
      }
      const string = apartment.mpc_vapautumispvm;
      const date = new Date(string);
      const stringLocalized = date.toLocaleDateString(globals.language);
      return fullSentences
        ? sprintf(translate("Vapautuu %s"), stringLocalized)
        : stringLocalized;
    },
    apartmentNextPresentation: function (apartment) {
      const presentations = Array.isArray(apartment?.presentations)
        ? apartment.presentations
        : [];
      return presentations[0] || null;
    },
    apartmentHasUpcomingShowing: function (apartment) {
      return Boolean(this.apartmentNextPresentation(apartment)?.dg_esittelyalkaa);
    },
    formatPresentationClock: function (iso) {
      const date = new Date(iso);
      if (Number.isNaN(date.getTime())) {
        return "";
      }
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${hours}:${minutes}`;
    },
    apartmentDisplayPresentation: function (presentation, includeEndTime = true) {
      if (!presentation?.dg_esittelyalkaa) {
        return "";
      }

      const start = new Date(presentation.dg_esittelyalkaa);
      if (Number.isNaN(start.getTime())) {
        return "";
      }

      const date = start.toLocaleDateString(globals.language);
      const startTime = this.formatPresentationClock(presentation.dg_esittelyalkaa);
      const endTime = includeEndTime && presentation.dg_esittelypaattyy
        ? this.formatPresentationClock(presentation.dg_esittelypaattyy)
        : "";
      const timeRange = endTime ? `${startTime} - ${endTime}` : startTime;

      return `${date} klo ${timeRange}`;
    },
    apartmentDisplayShowing: function (apartment) {
      const presentation = this.apartmentNextPresentation(apartment);
      if (!presentation?.dg_esittelyalkaa) {
        return null;
      }

      const date = new Date(presentation.dg_esittelyalkaa);
      if (Number.isNaN(date.getTime())) {
        return null;
      }

      const formattedDate = date.toLocaleDateString(globals.language, {
        day: "numeric",
        month: "numeric",
      });
      const startClock = this.formatPresentationClock(presentation.dg_esittelyalkaa);
      const endClock = presentation.dg_esittelypaattyy
        ? this.formatPresentationClock(presentation.dg_esittelypaattyy)
        : "";
      const clock = endClock
        ? `${startClock}-${endClock}`
        : startClock;
      const startTime = clock ? sprintf(translate("klo %s"), clock) : "";

      // fi: 9.10. klo 15:00-15:45
      return formattedDate + (startTime ? " " + startTime : "");
    },
    apartmentDisplayLocation: function (apartment) {
      const parts = [];

      if (apartment.township) {
        parts.push(apartment.township.name);
      }

      if (apartment.city) {
        parts.push(apartment.city.mpc_name);
      }

      return parts.join(", ");
    },
    apartmentDisplayArea(apartment, withUnit = true) {
      return formatStringArea(apartment.mpc_pinta_ala, withUnit);
    },
    apartmentDisplayFloorInfo(apartment) {
      if (!apartment.mpc_kerros) return '';

      const totalFloors = apartment.building?.mpc_maanpaallistenkerrostenlkm;
      if (totalFloors === 1) return '';

      const label = translate("Kerros");
      if (!totalFloors) return `${label} ${apartment.mpc_kerros}`;

      return `${label} ${apartment.mpc_kerros}/${totalFloors}`;
    },
    apartmentDisplaySellPrice(apartment) {
      if (apartment.type !== "owner-occupancy") return;
      return `${formatStringInt(apartment.ava_Myyntihinta)} €`;
    },
  },
};
